/**
 * ASSISTANT.UTILS
 * Un seul dialogue avec l'assistant, deux usages :
 * - askAssistantText : une valeur de champ, recue au fil de l'eau
 * - askAssistantJson : une reponse structuree, lue une fois complete
 * Les deux rendent la fonction qui coupe la conversation (arret utilisateur, demontage).
 */
import { Socket } from "socket.io-client"
import { SocketCreate, SocketEmit } from "./socketio.utils"

//Le texte va directement dans un champ : ni guillemets, ni preambule, ni conclusion
const TEXT_PREPROMPT =
  "Tu réponds uniquement par le texte demandé, sans guillemets, sans introduction et sans conclusion. Répondre en français."

export type AssistantCancel = () => void

/**
 * PROFILS DE MODELE
 * Le choix se lit ici, pas dans chaque appel. Mesures du 22/08/2026, sur les prompts reels de
 * l'app (premier mot / reponse complete) :
 * - JUGEMENT  gpt-5-mini minimal 549/881ms contre gpt-4o-mini 331/431ms. +200ms, et une vraie
 *   mesure de prevention la ou 4o-mini paraphrasait le risque. Jamais au-dela de "minimal" :
 *   "low" quadruple le delai (2026ms) sans rien apporter sur ces prompts.
 * - REFLEXE   quelques mots, l'utilisateur attend devant l'ecran. gpt-4o-mini tient mieux la
 *   consigne de format courte que les gpt-5, a vitesse egale : il reste en place.
 * - VISION    gpt-4o-mini et gpt-5 lisent l'image juste ; gpt-5-mini se trompe a tous les
 *   niveaux d'effort. A ne pas bouger sans test sur de vraies photos de terrain.
 */
export const ASSISTANT_JUDGMENT = { model: "gpt-5-mini", reasoningEffort: "minimal" }
export const ASSISTANT_INSTANT = { model: "gpt-4o-mini" }
export const ASSISTANT_VISION = { model: "gpt-4o-mini" }
//DOCUMENT  lecture d'un document (import d'un DUERP), par lots d'onglets : longue sortie structuree,
//          128k tokens de sortie chez les gpt-5. "low" : un Excel courant doit se lire en moins de
//          2 minutes ; revenir a "medium" si le rattachement des risques a leur unite se degrade.
export const ASSISTANT_DOCUMENT = { model: "gpt-5-mini", reasoningEffort: "low" }

interface AssistantProfile {
  model: string
  reasoningEffort?: string
}

interface AskParams {
  prompt: string
  preprompt?: string
  //Jugement metier par defaut : c'est ce que demandent tous les champs d'une fiche de risque
  profile?: AssistantProfile
  onError?: (error: any) => void
}

interface AskTextParams extends AskParams {
  //Appele a chaque arrivee de texte, avec tout ce qui a ete recu jusque la
  onToken?: (text: string) => void
  onDone: (text: string) => void
}

interface AskJsonParams extends AskParams {
  //Document joint (PDF), en data URL
  file?: { filename: string; fileData: string }
  onDone: (value: any) => void
}

//OpenAI garantit du JSON en mode json_object, mais un preprompt peut encore ramener des fences
export function cleanJson(value: unknown) {
  return String(value ?? "")
    .replace(/^```(?:json)?\s*/i, "")
    .replace(/\s*```$/, "")
    .trim()
}

/**
 * Consigne de brievete pour un champ qui nomme (intitule de risque, de mesure) : ces valeurs
 * s'affichent comme titres de carte, elles doivent tenir sur une ligne. "Compléter" est la pour
 * enrichir ensuite si l'utilisateur le veut.
 * Mesure du 22/08/2026 : sans consigne l'assistant rend 60 a 103 caracteres, avec elle 26 a 48.
 * Trois details tiennent le resultat :
 * - le plafond est donne en mots (les modeles comptent les mots, pas les caracteres),
 * - les exemples portent le format attendu : ce sont eux qui evitent la phrase complete,
 * - la consigne de grammaire evite le style telegraphique ("Coupures liees plans encombres"),
 *   dans lequel la contrainte de longueur fait tomber articles et prepositions.
 * Passer a 6 mots a ete teste : la reponse s'allonge sans mieux tenir la grammaire.
 */
export function getShortAnswerInstruction(examples: string[]) {
  return `Réponds en 5 mots maximum, sous forme de groupe nominal, sans verbe conjugué, en français grammaticalement correct (ne supprime pas les articles ni les prépositions). Exemples de format attendu : ${examples
    .map((example) => `« ${example} »`)
    .join(", ")}. Rien d'autre.`
}

/**
 * Consigne de format pour un champ de detail rendu en puces (« Détail des actions » d'une mesure).
 * Sans elle l'assistant ecrit des puces-paragraphes, illisibles dans un textarea de 5 lignes.
 * Memes leviers que getShortAnswerInstruction : le plafond est donne en mots (les modeles comptent
 * les mots, pas les lignes), et c'est l'exemple — pas la consigne — qui impose la ligne vide entre
 * les puces. 15 mots tiennent sur une ligne, deux au plus a la largeur du panneau lateral.
 * Le champ est un textarea brut, sans rendu markdown : la puce « • » est stockee telle quelle,
 * la ou un « - » resterait un tiret a l'ecran.
 */
export function getBulletAnswerInstruction() {
  return `Réponds par 3 puces maximum, chacune commençant par le caractère « • » et séparée de la suivante par une ligne vide. 15 mots maximum par puce : une puce tient sur une ligne, deux au plus. N'utilise ni tiret, ni astérisque, ni numéro pour marquer les puces. Pas de titre, pas de phrase d'introduction, pas de sous-puce. Exemple de format attendu :

• Choisir un modèle de couteau à lame rétractable.

• Équiper le poste de découpe.

• Former les opérateurs au nouveau modèle.`
}

/**
 * DEUX PASSES SUR LA LANGUE, ET LEUR FRONTIERE
 * Corriger  : les fautes, et rien d'autre. Les mots de l'utilisateur sont intouchables.
 * Reformuler: le meme sens, d'autres mots.
 *
 * LIMITE CONNUE ET ASSUMEE (mesures du 22/08/2026)
 * Sur un fragment de quelques mots, reformuler devine, et devine mal : « Plus de meubles » est
 * rendu « Absence de meubles », « Aucun meuble supplementaire » ou « D'avantage de meubles » selon
 * l'essai — trois lectures contradictoires du meme texte. « escabo case » devient « coince »,
 * « range » ou « enferme ».
 * Ce n'est pas rattrapable par le prompt : trois formulations ont ete testees, dont une disant
 * explicitement "ne devine jamais, rends le texte a l'identique", et le modele reformule quand
 * meme (0 texte conserve sur 3 essais, pour chacune des trois versions).
 * Le choix produit est de garder l'option toujours visible : un menu stable vaut mieux qu'une
 * entree qui apparait et disparait, et "Annuler la proposition" est a un clic. Ne pas remettre de
 * seuil de longueur, et ne pas esperer qu'une consigne suffise — seul un contexte metier (celui
 * que "Compléter" recoit) ferait cesser la devinette.
 *
 * Les deux passes ne demandent aucun contexte : un seul prompt sert tous les champs, profil REFLEXE.
 */

//Les consignes passent avant, le texte ferme le prompt entre balises. Place au milieu, un texte
//court ne se distingue plus des consignes qui le suivent : l'assistant les corrige et les recolle.
export function getFixPrompt(value: string) {
  return `Corrige uniquement les fautes d'orthographe, de grammaire, d'accord et de ponctuation du texte placé entre <texte> et </texte>.

Ne reformule rien : garde exactement les mêmes mots, le même ordre et la même structure. Ne remplace aucun mot par un synonyme, n'ajoute et ne supprime rien. Si le texte ne contient aucune faute, rends-le à l'identique.

Ne réponds que par le texte corrigé : pas de balises, pas de commentaire, et ne répète jamais ces consignes.

<texte>
${value}
</texte>`
}

export function getRephrasePrompt(value: string) {
  return `Reformule le texte placé entre <texte> et </texte> pour qu'il soit mieux écrit et plus clair.

Garde exactement le même sens et le même niveau de détail : aucune idée ajoutée, aucune idée retirée, aucun terme métier remplacé. Tu peux changer les mots, la tournure et l'ordre. Garde la même structure (puces, retours à la ligne) et une longueur équivalente. Si une formulation est ambiguë, garde-la telle quelle plutôt que de risquer d'en changer le sens.

Ne réponds que par le texte reformulé : pas de balises, pas de commentaire, et ne répète jamais ces consignes.

<texte>
${value}
</texte>`
}

export function askAssistantText({
  prompt,
  preprompt,
  profile,
  onToken,
  onDone,
  onError
}: AskTextParams): AssistantCancel {
  const socket: Socket = SocketCreate()
  let latestText = ""

  return SocketEmit(
    socket,
    "task-custom",
    {
      ...(profile ?? ASSISTANT_JUDGMENT),
      preprompt: preprompt ?? TEXT_PREPROMPT,
      prompt,
      isJson: false,
      sendMode: "chunk"
    },
    "fr",
    undefined,
    (text: string) => {
      latestText = text
      if (onToken) onToken(text)
    },
    (error: any) => {
      if (onError) onError(error)
    },
    //Les modeles finissent leurs lignes par des espaces (habitude markdown) : rien a garder en base
    () => onDone(latestText.replace(/[ \t]+$/gm, "").trim())
  ) as AssistantCancel
}

export function askAssistantJson({
  prompt,
  preprompt,
  profile,
  file,
  onDone,
  onError
}: AskJsonParams): AssistantCancel {
  const socket: Socket = SocketCreate()
  let latestResponse = ""

  return SocketEmit(
    socket,
    "task-custom",
    {
      ...(profile ?? ASSISTANT_JUDGMENT),
      preprompt: preprompt ?? "",
      prompt,
      file,
      sendMode: "all"
    },
    "fr",
    undefined,
    (response: string) => {
      latestResponse = response
    },
    (error: any) => {
      if (onError) onError(error)
    },
    () => {
      //La reponse est complete : la lire ici, jamais en cours de flux
      try {
        onDone(JSON.parse(cleanJson(latestResponse)))
      } catch (error) {
        if (onError) onError(error)
      }
    }
  ) as AssistantCancel
}
